import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import FocusTrap from 'focus-trap-react';

import ChevronDown from '@/icons/ChevronDown';
import { type INavItem } from '@/components/Header/Header.types';

import { PhoneIcon } from '@/icons';
import { commonTransition } from '@/components/Header/Header.animations';
import MobileMenuPanel from './MobileMenuPanel';
import MobileMenuTabs from './MobileMenuTabs';
import { type MobileMenuProps } from './MobileMenu.types';
import ButtonCircle from '../ButtonCircle';

const MobileMenu: React.FC<MobileMenuProps> = ({
  nav,
  subNav,
  loginCTA,
  loginMenu,
  headerHeight,
  transition,
  contactNumber,
  findStationUrl,
  findStationText,
}) => {
  const arrowHeight = 13;
  const arrowWidth = 17;
  const [pageIds, setPageIds] = useState<string[]>([]);
  const [levelTwoData, setLevelTwoData] = useState<INavItem>(undefined);
  const [levelThreeData, setLevelThreeData] = useState<INavItem>(undefined);
  const [categoryData, setCategoryData] = useState<INavItem[]>([]);
  const [levelOneLinks, setLevelOneLinks] = useState<INavItem[]>([]);
  const [loginMenuOpen, setLoginMenuOpen] = useState<boolean>(false);
  const showSubNav = subNav && subNav.length > 0;

  const panelVariants = {
    open: { x: 0 },
    closed: { x: '100%' },
  };

  const loginMenuBack = useRef(null);

  useEffect(() => {
    loginMenuBack.current?.focus();
  }, [loginMenuOpen]);

  /**
   * extract the relevant page data based on selected pages
   */
  useEffect(() => {
    const setNavigationData = () => {
      if (nav.length === 0) return;
      const categoryLinks = nav.filter((link) => !link.hidden);
      const topLevelLinks = nav.filter(
        (link) => link.linkingMode === 'current',
      );

      if (categoryLinks.length > 0) {
        categoryLinks.map((category) => {
          setCategoryData((prev) => [...prev, category]);
        });
        // split category children into single array of top level links
        categoryLinks.map((category) => {
          category.children.map((link) =>
            setLevelOneLinks((prev) => [...prev, link]),
          );
        });
      } else {
        topLevelLinks.map((link) =>
          setLevelOneLinks((prev) => [...prev, ...link.children]),
        );
      }
    };

    setNavigationData();

    return () => {
      setCategoryData([]);
      setLevelOneLinks([]);
    };
  }, [nav]);

  /**
   * extract the relevant L2 and L3 page data based on selected pages
   */
  useEffect(() => {
    const levelTwoData = levelOneLinks.find((item) => item.id === pageIds[0]);
    const levelThreeData = levelTwoData?.children?.find(
      (item) => item.id === pageIds[1],
    );
    setLevelTwoData(levelTwoData);
    setLevelThreeData(levelThreeData);

    return () => {
      setLevelTwoData(undefined);
      setLevelThreeData(undefined);
    };
  }, [pageIds]);

  useEffect(() => {
    const activePanel = document.getElementById('active-panel');

    if (activePanel && pageIds.length === 0) {
      activePanel.focus();
    }
  }, [pageIds]);

  /**
   * Toggle the login dropdown menu
   */
  const toggleLoginMenu = () => {
    setLoginMenuOpen(!loginMenuOpen);
  };

  return (
    <motion.div
      transition={transition}
      key="mobile-menu"
      initial={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: '100%' }}
      className="mobile-menu"
      style={{
        // dynamically set header offsets
        top: headerHeight,
        // avoid double scrolling if child panels are open
        overflowY: pageIds.length > 0 ? 'hidden' : 'auto',
      }}
    >
      <MobileMenuTabs
        categories={categoryData}
        onLinkClick={(id, title) => setPageIds([id])}
      />

      {showSubNav && (
        <ul className="mobile-menu__list mobile-menu__list--secondary">
          {subNav.map((item, i) => (
            <li key={i} className="mobile-menu__list-item">
              <a
                className={`mobile-menu__list-link ${item.linkingMode}`}
                href={item.url}
                target={item.isNewWindow && '_blank'}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ul>
      )}

      {loginCTA && (
        <>
          <div className="mobile-menu__login">
            <button
              id="mobile-login"
              className="mobile-menu__login--button button button--primary"
              onClick={toggleLoginMenu}
            >
              Login
              <ButtonCircle
                arrowHeight={arrowHeight}
                arrowWidth={arrowWidth}
                extraClasses="login-button"
              />
            </button>
          </div>

          <AnimatePresence initial={false}>
            {loginMenuOpen && (
              <motion.nav
                className="mobile-menu__nav"
                initial="closed"
                animate="open"
                exit="closed"
                variants={panelVariants}
                transition={commonTransition}
              >
                <FocusTrap>
                  <div className="mobile-menu__container">
                    <button
                      type="button"
                      ref={loginMenuBack}
                      className="mobile-menu__back-button"
                      onClick={toggleLoginMenu}
                    >
                      <ChevronDown
                        className="mobile-menu__back-icon"
                        fill="currentColor"
                        width={8}
                      />
                      Back
                    </button>

                    <ul className="mobile-menu__list">
                      {loginMenu.map((link, i) => {
                        const { title, url, isNewWindow } = link;

                        return (
                          <li key={i} className="mobile-menu__list-item">
                            <a
                              className="mobile-menu__list-link"
                              href={url}
                              target={isNewWindow && '_blank'}
                            >
                              {title}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </FocusTrap>
              </motion.nav>
            )}
          </AnimatePresence>
        </>
      )}

      {contactNumber && (
        <div className="mobile-menu__contact">
          <a
            className="mobile-menu__contact-link"
            href={`tel:${contactNumber}`}
          >
            <PhoneIcon className="icon" />
            <strong>Call Us</strong> <span>{contactNumber}</span>
          </a>
        </div>
      )}

      <AnimatePresence>
        {pageIds.length > 0 && (
          <MobileMenuPanel
            key="mobile-menu-panel-1"
            modifier="first"
            pages={pageIds}
            activePage={levelTwoData}
            onBackClick={() => setPageIds([...pageIds.slice(0, -1)])}
            onLinkClick={(id) => setPageIds([...pageIds, id])}
          />
        )}

        {pageIds.length > 1 && (
          <MobileMenuPanel
            key="mobile-menu-panel-2"
            isLast
            modifier="second"
            pages={pageIds}
            activePage={levelThreeData}
            backText={levelTwoData?.title}
            onBackClick={() => setPageIds([...pageIds.slice(0, -1)])}
            onLinkClick={(id) => setPageIds([...pageIds, id])}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MobileMenu;
